/**
 * Cognito Token Manager - Manejo de renovación de tokens de AWS Cognito
 * Implementa renovación automática de tokens de Cognito cuando expiran
 */
import { CognitoUser } from 'amazon-cognito-identity-js';
import { Auth } from 'aws-amplify';
import { isTokenExpired } from './isTokenExpired';

interface CognitoTokenPair {
    accessToken: string;
    idToken: string;
    refreshToken: string;
}

interface CognitoTokenManager {
    setTokens(tokenPair: CognitoTokenPair): void;
    getValidAccessToken(): Promise<string | null>;
    getValidIdToken(): Promise<string | null>;
    refreshTokens(): Promise<CognitoTokenPair>;
    clearTokens(): void;
    isAuthenticated(): boolean;
    isTokenExpired(): boolean;
}

class CognitoTokenManagerImpl implements CognitoTokenManager {
    private accessToken: string | null = null;

    private idToken: string | null = null;

    private refreshToken: string | null = null;

    private cognitoUser: CognitoUser | null = null;

    private refreshPromise: Promise<CognitoTokenPair> | null = null;

    constructor() {
        this.initializeTokens();
    }

    /**
     * Inicializa los tokens desde el almacenamiento
     */
    private initializeTokens(): void {
        const serviceToken = localStorage.getItem('serviceToken');
        const idToken = localStorage.getItem('idToken');
        const refreshToken = localStorage.getItem('cognitoRefreshToken');

        if (serviceToken && idToken && refreshToken) {
            this.accessToken = serviceToken;
            this.idToken = idToken;
            this.refreshToken = refreshToken;
        }
    }

    /**
     * Establece un nuevo conjunto de tokens de Cognito
     */
    public setTokens(tokenPair: CognitoTokenPair): void {
        this.accessToken = tokenPair.accessToken;
        this.idToken = tokenPair.idToken;
        this.refreshToken = tokenPair.refreshToken;

        // Guardar en localStorage para persistencia
        localStorage.setItem('serviceToken', tokenPair.accessToken);
        localStorage.setItem('idToken', tokenPair.idToken);
        localStorage.setItem('cognitoRefreshToken', tokenPair.refreshToken);

        // Limpiar cualquier promesa de renovación pendiente
        this.refreshPromise = null;
    }

    /**
     * Obtiene el access token actual
     */
    public getAccessToken(): string | null {
        return this.accessToken;
    }

    /**
     * Obtiene el ID token actual
     */
    public getIdToken(): string | null {
        return this.idToken;
    }

    /**
     * Verifica si el access token está expirado
     */
    public isTokenExpired(): boolean {
        if (!this.accessToken) {
            return true;
        }
        return isTokenExpired(this.accessToken);
    }

    /**
     * Obtiene un access token válido (renovando si es necesario)
     */
    public async getValidAccessToken(): Promise<string | null> {
        if (!this.accessToken || this.isTokenExpired()) {
            try {
                const newTokens = await this.refreshTokens();
                return newTokens.accessToken;
            } catch (error) {
                console.error('Error renovando access token de Cognito:', error);
                return null;
            }
        }
        return this.accessToken;
    }

    /**
     * Obtiene un ID token válido (renovando si es necesario)
     */
    public async getValidIdToken(): Promise<string | null> {
        if (!this.idToken || this.isTokenExpired()) {
            try {
                const newTokens = await this.refreshTokens();
                return newTokens.idToken;
            } catch (error) {
                console.error('Error renovando ID token de Cognito:', error);
                return null;
            }
        }
        return this.idToken;
    }

    /**
     * Renueva los tokens de Cognito usando el refresh token
     */
    public async refreshTokens(): Promise<CognitoTokenPair> {
        // Si ya hay una renovación en progreso, esperar a que termine
        if (this.refreshPromise) {
            return this.refreshPromise;
        }

        // Crear nueva promesa de renovación
        this.refreshPromise = this.performTokenRefresh();

        try {
            const result = await this.refreshPromise;
            return result;
        } finally {
            this.refreshPromise = null;
        }
    }

    /**
     * Realiza la renovación de tokens usando AWS Amplify
     */
    private async performTokenRefresh(): Promise<CognitoTokenPair> {
        try {
            // Obtener el usuario actual de Cognito
            const currentUser = await Auth.currentAuthenticatedUser();
            this.cognitoUser = currentUser;

            // AWS Amplify maneja automáticamente la renovación de tokens
            // currentSession() obtiene la sesión actual y renueva tokens si es necesario
            const session = await Auth.currentSession();

            // Verificar si la sesión es válida
            if (!session.isValid()) {
                throw new Error('Cognito session is not valid');
            }

            // Obtener los tokens renovados
            const accessToken = session.getAccessToken().getJwtToken();
            const idToken = session.getIdToken().getJwtToken();
            const refreshToken = session.getRefreshToken().getToken();

            const tokenPair: CognitoTokenPair = {
                accessToken,
                idToken,
                refreshToken
            };

            // Actualizar tokens
            this.setTokens(tokenPair);

            console.log('✅ Tokens de Cognito renovados exitosamente usando AWS Amplify');
            return tokenPair;
        } catch (error) {
            console.error('❌ Error renovando tokens de Cognito:', error);

            // Limpiar tokens en caso de error
            this.clearTokens();
            throw error;
        }
    }

    /**
     * Limpia todos los tokens
     */
    public clearTokens(): void {
        this.accessToken = null;
        this.idToken = null;
        this.refreshToken = null;
        this.cognitoUser = null;
        this.refreshPromise = null;

        // Limpiar localStorage
        localStorage.removeItem('serviceToken');
        localStorage.removeItem('idToken');
        localStorage.removeItem('cognitoRefreshToken');
    }

    /**
     * Verifica si el usuario está autenticado
     */
    public isAuthenticated(): boolean {
        return this.accessToken !== null && !this.isTokenExpired();
    }

    /**
     * Obtiene información del usuario actual
     */
    public async getCurrentUser(): Promise<CognitoUser | null> {
        try {
            if (!this.cognitoUser) {
                this.cognitoUser = await Auth.currentAuthenticatedUser();
            }
            return this.cognitoUser;
        } catch (error) {
            console.error('Error obteniendo usuario actual de Cognito:', error);
            return null;
        }
    }

    /**
     * Verifica si los tokens necesitan renovación (con margen de tiempo)
     */
    public shouldRefreshTokens(marginMinutes: number = 5): boolean {
        if (!this.accessToken) {
            return true;
        }

        try {
            const payload = JSON.parse(atob(this.accessToken.split('.')[1]));
            const exp = payload.exp * 1000; // Convertir a milisegundos
            const now = Date.now();
            const marginMs = marginMinutes * 60 * 1000;

            return exp - now < marginMs;
        } catch (error) {
            console.error('Error verificando expiración de token:', error);
            return true;
        }
    }
}

// Instancia singleton del CognitoTokenManager
export const cognitoTokenManager = new CognitoTokenManagerImpl();
export default cognitoTokenManager;
